import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createCostModel,
  getCostModel,
  listCostModels,
  updateCostModel,
  type CostModelDetail,
  type CostModelSummary,
  type CreateCostModelBody,
  type UpdateCostModelBody,
} from "../api/costModels";

const KEY = "cost-models";

/** Summary list of the 13 cost models from svc-10 `/v1/cost-models`. */
export function useCostModels() {
  return useQuery<CostModelSummary[]>({
    queryKey: [KEY, "list"],
    queryFn: () => listCostModels(),
    staleTime: 30_000,
  });
}

/** Full detail of one cost model from svc-10 `/v1/cost-models/:id`. */
export function useCostModel(id: string | undefined) {
  return useQuery<CostModelDetail>({
    queryKey: [KEY, "detail", id],
    queryFn: () => getCostModel(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** POST /v1/cost-models — create a new model; refreshes the list. */
export function useCreateCostModel() {
  const queryClient = useQueryClient();
  return useMutation<CostModelDetail, Error, CreateCostModelBody>({
    mutationFn: (body) => createCostModel(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/** PUT /v1/cost-models/:id — replace the writable blocks in one transaction. */
export function useUpdateCostModel() {
  const queryClient = useQueryClient();
  return useMutation<
    CostModelDetail,
    Error,
    { id: string; body: UpdateCostModelBody }
  >({
    mutationFn: ({ id, body }) => updateCostModel(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
